import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import { Box, Typography } from '@mui/material';
import type { Theme } from '@mui/material/styles';
import type { TFunction } from 'i18next';
import React, { useId } from 'react';
import { useTranslation } from 'react-i18next';

import { ButterCup, Neutrals, Primary, SemanticError } from '../../../../core-components/theme/color-palette/colors';
import { ChoiceChipGroup } from '../../../../core-components/theme/components/Fields';
import { settingsRowSx } from '../../../../core-components/theme/components/SettingsList';
import { modeLabel, severityLabel } from '../../constants/labels';
import { MODE_COLUMN, severityRowGridSx } from '../../constants/layout';
import type { RowNoteKey, RowWarningKey } from '../../domain/severityRows';
import type { DeliveryMode, ModeChoices, Severity } from '../../types/domain.types';
import { SeverityChip } from '../shared/SeverityChip';

import { rowNoteText, rowWarningText } from './settingsText';

const lockSx = { color: Neutrals[600] } as const;
const rowSx = [settingsRowSx, severityRowGridSx] as const;
const identitySx = { display: 'flex', flexDirection: 'column', gap: 1, minInlineSize: 0 } as const;
const chipLineSx = { display: 'flex', flexWrap: 'wrap', alignItems: 'center', columnGap: 2, rowGap: 1 } as const;
const changedSx = { color: Primary[600] } as const;
const effectSx = { color: Neutrals[600] } as const;
const choicesSx = { display: 'flex', flexDirection: 'column', gap: 2, minInlineSize: 0 } as const;
const valueLineSx = { display: 'flex', alignItems: 'center', gap: 1, minBlockSize: (theme: Theme) => theme.controlHeight.small } as const;
const valueSx = { color: Neutrals[900] } as const;
const textHelperSx = { color: Neutrals[600] } as const;
const textErrorSx = { color: SemanticError[600] } as const;
const warningSx = {
  display: 'flex',
  alignItems: 'flex-start',
  gap: 2,
  p: 2,
  borderRadius: 1.5,
  backgroundColor: ButterCup[50],
  color: ButterCup[900],
} as const;
const warningIconSx = { flexShrink: 0 } as const;
const warningTextSx = { color: 'inherit' } as const;

const columnOf = (mode: DeliveryMode): number => MODE_COLUMN[mode];

const modeOptionsOf = (t: TFunction, choices: ModeChoices) =>
  choices.options.map((option) => ({ value: option.mode, label: modeLabel(t, option.mode), disabled: option.disabled }));

const joinIds = (ids: ReadonlyArray<string | null>): string | undefined => {
  const present = ids.filter((id): id is string => id !== null);
  return present.length === 0 ? undefined : present.join(' ');
};

export interface SeverityRowProps {
  severity: Severity;
  choices: ModeChoices;
  value: DeliveryMode;
  helperKey: RowNoteKey | null;
  warningKey?: RowWarningKey | null;
  violationMessage: string | null;
  readOnly: boolean;
  saving?: boolean;
  onChange: (mode: DeliveryMode) => void;
  changed?: boolean;
  effect?: string;
}

interface RowValueTextProps {
  readonly value: DeliveryMode;
  readonly helper: string | undefined;
  readonly isError: boolean;
}

const RowHelper: React.FC<{ helper: string | undefined; isError: boolean }> = ({ helper, isError }) => {
  if (helper === undefined) return null;
  return (
    <Typography variant="label" component="p" sx={isError ? textErrorSx : textHelperSx}>
      {helper}
    </Typography>
  );
};

const RowValueText: React.FC<RowValueTextProps> = ({ value, helper, isError }) => {
  const { t } = useTranslation();
  return (
    <>
      <Box sx={valueLineSx}>
        <Typography variant="highlight" component="span" sx={valueSx}>
          {modeLabel(t, value)}
        </Typography>
        <LockOutlinedIcon fontSize="small" aria-hidden data-testid="severity-row-lock" sx={lockSx} />
      </Box>
      <RowHelper helper={helper} isError={isError} />
    </>
  );
};

interface RowChoicesProps {
  readonly props: SeverityRowProps;
  readonly helper: string | undefined;
  readonly describedById: string | undefined;
}

const RowChoices: React.FC<RowChoicesProps> = ({ props, helper, describedById }) => {
  const { t } = useTranslation();
  const { severity, choices, value, violationMessage, readOnly, saving = false, onChange } = props;
  if (choices.locked || readOnly) return <RowValueText value={value} helper={helper} isError={violationMessage !== null} />;
  return (
    <ChoiceChipGroup
      label={severityLabel(t, severity)}
      hideLabel
      options={modeOptionsOf(t, choices)}
      value={value}
      onChange={onChange}
      columns={3}
      columnOf={columnOf}
      frozen={saving}
      error={violationMessage !== null}
      helperText={helper}
      describedById={describedById}
    />
  );
};

const RowWarning: React.FC<{ id: string; warning: string | null }> = ({ id, warning }) => {
  if (warning === null) return null;
  return (
    <Box id={id} sx={warningSx}>
      <WarningAmberOutlinedIcon fontSize="small" aria-hidden sx={warningIconSx} />
      <Typography variant="label" component="span" sx={warningTextSx}>
        {warning}
      </Typography>
    </Box>
  );
};

export const SeverityRow: React.FC<SeverityRowProps> = (props) => {
  const { severity, helperKey, warningKey = null, violationMessage, changed = false, effect } = props;
  const { t } = useTranslation();
  const baseId = useId();
  const effectId = `${baseId}-effect`;
  const changedId = `${baseId}-changed`;
  const warningId = `${baseId}-warning`;

  const warning = warningKey === null ? null : rowWarningText(t, warningKey);
  const helper = violationMessage ?? (helperKey === null ? undefined : rowNoteText(t, helperKey));
  const describedById = joinIds([effect === undefined ? null : effectId, changed ? changedId : null, warning === null ? null : warningId]);

  return (
    <Box component="li" sx={rowSx}>
      <Box sx={identitySx}>
        <Box sx={chipLineSx}>
          <SeverityChip severity={severity} />
          {changed && (
            <Typography id={changedId} variant="caption2" component="span" sx={changedSx}>
              {t('closeTheLoop.settings.changed', 'Changed')}
            </Typography>
          )}
        </Box>
        {effect !== undefined && (
          <Typography id={effectId} variant="label" component="span" sx={effectSx}>
            {effect}
          </Typography>
        )}
      </Box>
      <Box sx={choicesSx}>
        <RowChoices props={props} helper={helper} describedById={describedById} />
        <RowWarning id={warningId} warning={warning} />
      </Box>
    </Box>
  );
};
